import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  MapPin,
  Camera as CameraIcon,
  Images,
  Bell,
  HeartPulse,
  Compass,
  Settings as SettingsIcon,
  Check,
  ArrowLeft,
  BatteryCharging,
  Crosshair,
  Wifi,
  Layers,
  MoonStar,
  CloudOff,
  ShieldCheck,
  Sparkles,
  LucideIcon,
} from "lucide-react";
import {
  PrimaryButton,
  SecondaryButton,
  TextButton,
  StatusChip,
  type StatusChipVariant,
} from "@/components/roava";
import {
  checkLocationPermission,
  requestLocationAlways,
  checkPhotoLibraryPermission,
  requestPhotoLibraryReadWrite,
  requestNotificationsPermission,
  type PermissionState,
} from "@/lib/native/permissions";
import { Camera } from "@capacitor/camera";
import { LocalNotifications } from "@capacitor/local-notifications";
import { isNative } from "@/lib/native/platform";
import {
  HealthBridge,
  isHealthAvailable,
  requestHealthWriteAuth,
  type HealthAuthStatus,
} from "@/lib/native/health";
import { openAppSettings } from "@/lib/native/open-app-settings";
import {
  markOnboardingComplete,
  markOnboardingSkipped,
} from "@/lib/onboarding/state";
import { cn } from "@/lib/utils";


import { ShowcaseFrame } from "./showcases/ShowcaseFrame";
import { RouteGlobePreview } from "./showcases/RouteGlobePreview";
import { UploadPreview } from "./showcases/UploadPreview";
import { PodsPreview } from "./showcases/PodsPreview";
import { TimelinePreview } from "./showcases/TimelinePreview";
import { SettingDeepDive } from "./SettingDeepDive";
import { MiniDemosSheet } from "./MiniDemosSheet";
import { SampleTripPicker } from "./SampleTripPicker";

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

type PermKey = "location" | "camera" | "photos" | "notifications" | "health";
type ShowcaseKind = "globe" | "upload" | "pods" | "timeline";
type DeepDiveKind =
  | "battery"
  | "accuracy"
  | "uploadLimits"
  | "podGrouping"
  | "quietHours"
  | "offline"
  | "privacy";

type StepDef = {
  id: string;
  title: string;
  body: string;
  /** Extended-mode replacement body (plainer, longer). */
  bodyExtended?: string;
  icon: LucideIcon;
  perm?: PermKey;
  whereHint?: string;
  showcase?: ShowcaseKind;
  showcaseCaption?: string;
  showcaseCaptions?: string[];
  deepDive?: DeepDiveKind;
  /** If true, only rendered in extended mode. */
  extendedOnly?: boolean;
  /** Final "ready to land in app" step. */
  finish?: boolean;
};

/* ------------------------------------------------------------------ */
/* Step list                                                          */
/* ------------------------------------------------------------------ */

const STEPS: StepDef[] = [
  {
    id: "welcome",
    title: "Welcome to Roava",
    body: "Roava tracks your trips, captures photos along the way, and keeps every memory organized by route.",
    bodyExtended:
      "Roava records your route while you travel, ties each photo to where you took it, and turns the whole trip into a tidy day-by-day timeline. Pick the tour that feels right — you can switch any time.",
    icon: Compass,
  },

  /* ---- Location ---- */
  {
    id: "tracking",
    title: "Route tracking",
    body: "The Route tab is where you start and stop trips and watch your live map. Roava needs Location to record your route, including in the background while your phone is in your pocket.",
    bodyExtended:
      "Roava draws the line you walked, drove or rode on a globe. To do that, it needs permission to see your location — even when the app isn't open — so the line stays connected while your phone is locked or in your pocket.",
    icon: MapPin,
    perm: "location",
    whereHint: "Adjust later in Settings → Trip readiness → Route tracking.",
  },
  {
    id: "battery-gating",
    title: "Battery-smart tracking",
    body: "",
    bodyExtended:
      "When your phone drops below 15% battery, Roava quietly pauses background tracking so you don't run out of power. It picks back up automatically when you charge.",
    icon: BatteryCharging,
    deepDive: "battery",
    extendedOnly: true,
  },
  {
    id: "showcase-globe",
    title: "Your trips on the globe",
    body: "",
    bodyExtended: "",
    icon: Compass,
    showcase: "globe",
    showcaseCaption: "Each line is one trip. Dots mark the stops where you took photos.",
    showcaseCaptions: [
      "This is the globe view in the Route tab.",
      "Each glowing line is one trip you took.",
      "Dots mark the stops where you took photos.",
    ],
  },
  {
    id: "accuracy",
    title: "Live route accuracy",
    body: "",
    bodyExtended:
      "By default Roava uses balanced accuracy to save battery. On long drives or hikes you can switch to high-accuracy for a smoother line — Settings remembers your choice per trip.",
    icon: Crosshair,
    deepDive: "accuracy",
    extendedOnly: true,
  },

  /* ---- Camera ---- */
  {
    id: "camera",
    title: "Capture photos",
    body: "On a trip, tap the camera to capture moments tied to your current location and route.",
    bodyExtended:
      "Tap the camera button on any trip screen to take a picture. Roava stamps it with your exact spot on the route, so it slots into the right pod automatically.",
    icon: CameraIcon,
    perm: "camera",
    whereHint: "Adjust later in iOS Settings → Roava → Camera.",
  },
  {
    id: "showcase-upload",
    title: "Add photos you already took",
    body: "",
    bodyExtended: "",
    icon: Images,
    showcase: "upload",
    showcaseCaption: "Drop or pick photos and Roava sorts them onto your route by their timestamps.",
    showcaseCaptions: [
      "This is the Upload tab.",
      "Drag photos in, or tap to pick from your library.",
      "Roava reads the time on each photo and places it on the right stop.",
    ],
  },

  /* ---- Photos ---- */
  {
    id: "photos",
    title: "Photos & Upload",
    body: "The Upload tab lets you add pictures from your library. Roava queues big uploads and reminds you when Wi-Fi is a better choice.",
    bodyExtended:
      "Roava can read photos from your Camera Roll so it can match them to your route. Granting access is read-only unless you choose to save things back — it doesn't move or delete anything.",
    icon: Images,
    perm: "photos",
    whereHint: "Adjust later in Settings → Trip readiness → Photos & videos.",
  },
  {
    id: "upload-limits",
    title: "Wi-Fi vs cellular uploads",
    body: "",
    bodyExtended:
      "By default Roava only uploads big batches when you're on Wi-Fi so it doesn't burn your data. You can flip it to allow cellular if you want photos to sync the moment you take them.",
    icon: Wifi,
    deepDive: "uploadLimits",
    extendedOnly: true,
  },
  {
    id: "showcase-pods",
    title: "Photos cluster into Pods",
    body: "",
    bodyExtended: "",
    icon: Layers,
    showcase: "pods",
    showcaseCaption: "Roava groups nearby photos into a Pod at each stop, so your gallery isn't a wall of thumbnails.",
    showcaseCaptions: [
      "A Pod is a group of photos taken in roughly the same place.",
      "Each card shows the location and time of the stop.",
      "Tap a Pod to see every photo from that stop.",
    ],
  },
  {
    id: "pod-grouping",
    title: "How Pods are grouped",
    body: "",
    bodyExtended:
      "Roava starts a new Pod when you've moved far enough or stopped taking photos for long enough. You can tighten or loosen those thresholds — handy for city walks vs road trips.",
    icon: Layers,
    deepDive: "podGrouping",
    extendedOnly: true,
  },

  /* ---- Notifications ---- */
  {
    id: "notifications",
    title: "Trip & upload reminders",
    body: "Roava sends helpful pings — trip status, upload reminders, and quiet-hours-aware nudges. You're always in control.",
    bodyExtended:
      "These are little nudges like 'your trip is still recording' or 'you've got 12 photos ready to upload on Wi-Fi'. Nothing chatty — and quiet hours are respected.",
    icon: Bell,
    perm: "notifications",
    whereHint: "Adjust later in Settings → Notifications.",
  },
  {
    id: "showcase-timeline",
    title: "Your trip, day by day",
    body: "",
    bodyExtended: "",
    icon: Sparkles,
    showcase: "timeline",
    showcaseCaption: "Each stop becomes a day on your timeline with the photos you took there.",
    showcaseCaptions: [
      "After a trip, Roava builds this timeline automatically.",
      "Stops are grouped by day with the photos from each stop.",
      "Great for sharing with family who weren't there.",
    ],
  },
  {
    id: "quiet-hours",
    title: "Quiet hours",
    body: "",
    bodyExtended:
      "Notifications stay silent between 9pm and 7am by default. Trip-critical alerts still come through — everything else waits until morning.",
    icon: MoonStar,
    deepDive: "quietHours",
    extendedOnly: true,
  },

  /* ---- Health ---- */
  {
    id: "health",
    title: "Apple Health (optional)",
    body: "Save a recorded track as a Health workout so distance and route show up in Fitness. Totally optional.",
    bodyExtended:
      "If you use Apple Health or the Fitness app, Roava can drop a finished trip in as a workout so it counts toward your rings and shows the route. Totally optional — skip and it changes nothing.",
    icon: HeartPulse,
    perm: "health",
    whereHint: "Adjust later in iOS Settings → Health → Data Access.",
  },
  {
    id: "offline",
    title: "Offline mode",
    body: "",
    bodyExtended:
      "Roava keeps recording your route even with no signal — points queue on the phone and sync when you're back online. Before a remote trip you can pre-cache map tiles for the area.",
    icon: CloudOff,
    deepDive: "offline",
    extendedOnly: true,
  },

  /* ---- Settings ---- */
  {
    id: "settings",
    title: "Where everything lives",
    body: "All of these controls — tracking, uploads, privacy, notifications, account — live in Settings (the gear tab). You can change anything later, anytime.",
    bodyExtended:
      "Every toggle you've seen lives in Settings (the gear icon at the bottom of the app). Nothing is buried — and you can replay this walkthrough whenever you like from Settings → Replay app walkthrough.",

    icon: SettingsIcon,
    whereHint: "Look for the gear icon in the bottom tab bar.",
  },
  {
    id: "privacy",
    title: "Privacy & sharing",
    body: "",
    bodyExtended:
      "Your trips are private to you unless you join a group trip. Only people you invite to a trip can see its data — Roava staff can't read your photos or routes.",
    icon: ShieldCheck,
    deepDive: "privacy",
    extendedOnly: true,
  },

  /* ---- Finish ---- */
  {
    id: "done",
    title: "Want to see Roava with a trip in it?",
    body: "Pick any of these to drop a ready-made trip into your account — with route, photo pods and a timeline. Add as many as you like, or skip.",
    bodyExtended:
      "That's the whole tour. Pick any of these curated trips to drop a ready-made trip into your account — route, photo pods and a timeline included. You can add more than one, and remove them anytime from Settings → Account.",
    icon: Check,
    finish: true,
  },
];

/* ------------------------------------------------------------------ */
/* Permission status                                                  */
/* ------------------------------------------------------------------ */

type AnyStatus = PermissionState | HealthAuthStatus | "unknown";

async function readStatus(key: PermKey): Promise<AnyStatus> {
  try {
    switch (key) {
      case "location":
        return await checkLocationPermission();
      case "photos":
        return await checkPhotoLibraryPermission();
      case "camera": {
        if (!isNative()) return "unsupported";
        const r = await Camera.checkPermissions();
        return (r.camera as PermissionState) ?? "prompt";
      }
      case "notifications": {
        if (!isNative()) return "unsupported";
        const r = await LocalNotifications.checkPermissions();
        return (r.display as PermissionState) ?? "prompt";
      }
      case "health": {
        if (!isNative()) return "unsupported";
        if (!(await isHealthAvailable())) return "unavailable";
        const r = await HealthBridge.getAuthStatus();
        return (r?.status as HealthAuthStatus) ?? "unknown";
      }
    }
  } catch {
    return "unsupported";
  }
}

async function runRequest(key: PermKey): Promise<void> {
  try {
    switch (key) {
      case "location": await requestLocationAlways(); break;
      case "camera": if (isNative()) await Camera.requestPermissions({ permissions: ["camera"] }); break;
      case "photos": await requestPhotoLibraryReadWrite(); break;
      case "notifications": await requestNotificationsPermission(); break;
      case "health": await requestHealthWriteAuth(); break;
    }
  } catch { /* status re-read still happens */ }
}

type StatusKind = "unknown" | "prompt" | "granted" | "limited" | "denied" | "unavailable";

function classify(state: AnyStatus): StatusKind {
  switch (state) {
    case "granted":
    case "authorized_write":
    case "denied_write":
      return "granted";
    case "limited": return "limited";
    case "denied": return "denied";
    case "unavailable": return "unavailable";
    case "prompt": return "prompt";
    case "unknown":
    case "unsupported":
    default: return "unknown";
  }
}

function chipForKind(kind: StatusKind): { variant: StatusChipVariant; label: string } | null {
  switch (kind) {
    case "granted": return { variant: "success", label: "Enabled" };
    case "limited": return { variant: "info", label: "Limited access" };
    case "denied": return { variant: "danger", label: "Blocked" };
    case "unavailable": return { variant: "neutral", label: "Not available" };
    default: return null;
  }
}

function labelFor(key: PermKey): string {
  switch (key) {
    case "location": return "Location";
    case "camera": return "Camera";
    case "photos": return "Photos";
    case "notifications": return "Notifications";
    case "health": return "Health";
  }
}

/* ------------------------------------------------------------------ */
/* Walkthrough                                                        */
/* ------------------------------------------------------------------ */

type TourMode = "quick" | "extended" | null;

export function OnboardingWalkthrough({ onClose }: { onClose: () => void }) {
  const [tourMode, setTourMode] = useState<TourMode>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [status, setStatus] = useState<AnyStatus>("unknown");
  const [pending, setPending] = useState(false);
  const [miniDemosOpen, setMiniDemosOpen] = useState(false);
  
  const navigate = useNavigate();

  // Visible steps depend on mode (extended sees deep-dives; quick skips them).
  const visibleSteps = STEPS.filter((s) => {
    if (s.extendedOnly && tourMode !== "extended") return false;
    return true;
  });

  // Clamp index when mode changes (e.g. user toggles quick<->extended mid-tour).
  useEffect(() => {
    if (stepIdx > visibleSteps.length - 1) setStepIdx(visibleSteps.length - 1);
  }, [tourMode, visibleSteps.length, stepIdx]);

  const step = visibleSteps[stepIdx] ?? visibleSteps[0];
  const isWelcome = step.id === "welcome";
  const isLast = step.finish === true;

  useEffect(() => {
    let cancelled = false;
    const key = step.perm;
    if (!key) { setStatus("unknown"); return; }
    const read = async () => {
      const s = await readStatus(key);
      if (!cancelled) setStatus(s);
    };
    void read();
    const onFocus = () => { void read(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [step.perm]);

  const kind = step.perm ? classify(status) : "unknown";
  const chip = step.perm ? chipForKind(kind) : null;

  const finishTour = () => {
    markOnboardingComplete();
    try { void navigate({ to: "/permissions" }); } catch { /* noop */ }
    onClose();
  };

  const skipToEnd = () => {
    setStepIdx(visibleSteps.length - 1);
  };

  const handleSkip = () => {
    markOnboardingSkipped();
    onClose();
  };

  const goNext = () => {
    setStepIdx((i) => Math.min(i + 1, visibleSteps.length - 1));
  };
  const goBack = () => {
    setStepIdx((i) => Math.max(i - 1, 0));
  };

  // (sample seeding moved into SampleTripPicker on the final step)

  /* Primary CTA */
  const primary = (() => {
    if (isWelcome) {
      // Welcome handled separately (two side-by-side buttons)
      return null;
    }
    if (isLast) {
      // On the final step the SampleTripPicker renders its own buttons; we
      // hide the generic primary CTA below.
      return null;
    }
    if (step.showcase || step.deepDive) {
      return { label: "Continue", onClick: goNext };
    }
    if (!step.perm) return { label: "Continue", onClick: goNext };

    if (kind === "granted" || kind === "limited" || kind === "unavailable") {
      return { label: "Continue", onClick: goNext };
    }
    if (kind === "denied") {
      return { label: "Open Settings", onClick: async () => { try { await openAppSettings(); } catch { /* noop */ } } };
    }
    return {
      label: pending ? "Requesting…" : `Enable ${labelFor(step.perm)}`,
      onClick: async () => {
        if (!step.perm) return;
        setPending(true);
        await runRequest(step.perm);
        const s = await readStatus(step.perm);
        setStatus(s);
        setPending(false);
      },
    };
  })();

  /* Secondary */
  const secondary = (() => {
    if (isWelcome) return null;
    if (isLast) return null;
    if (step.showcase || step.deepDive) return { label: "Skip Tutorial", onClick: skipToEnd };
    if (!step.perm) return { label: "Skip Tutorial", onClick: skipToEnd };
    if (kind === "granted" || kind === "limited" || kind === "unavailable") {
      return { label: "Skip Tutorial", onClick: skipToEnd };
    }
    if (kind === "denied") return { label: "Continue anyway", onClick: goNext };
    return { label: "Skip Tutorial", onClick: skipToEnd };
  })();

  const isExtended = tourMode === "extended";
  const titleClass = isExtended ? "text-[26px] leading-tight" : "text-2xl";
  const bodyClass = isExtended ? "text-[17px] leading-relaxed" : "text-sm leading-relaxed";
  const bodyText = isExtended && step.bodyExtended ? step.bodyExtended : step.body;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="roava-onboarding-title"
    >
      <div
        className="w-full max-w-[480px] rounded-t-3xl sm:rounded-3xl bg-[var(--roava-bg)] shadow-2xl border border-[var(--roava-border)] flex flex-col max-h-[92vh]"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
      >
        {/* Header — step indicator + skip / back */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 gap-3">
          {!isWelcome && (isExtended || isLast) ? (
            <TextButton
              tone="muted"
              onClick={goBack}
              className="text-xs inline-flex items-center gap-1"
              disabled={stepIdx === 0}
            >
              <ArrowLeft className="size-3.5" /> Back
            </TextButton>
          ) : (
            <span className="text-xs text-[var(--roava-text-muted)]">
              {isWelcome ? "Welcome" : `Step ${stepIdx + 1} of ${visibleSteps.length}`}
            </span>
          )}
          <div className="flex items-center gap-1 flex-1 justify-center max-w-[220px]" aria-hidden="true">
            {visibleSteps.map((_, i) => (
              <span
                key={i}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === stepIdx
                    ? "w-5 bg-[var(--roava-blue)]"
                    : i < stepIdx
                      ? "w-1.5 bg-[var(--roava-blue)]/60"
                      : "w-1.5 bg-[var(--roava-border)]",
                )}
              />
            ))}
          </div>
          {!isLast && !isWelcome ? (
            <TextButton tone="muted" onClick={skipToEnd} className="text-xs">
              Skip Tutorial
            </TextButton>
          ) : !isLast && isWelcome ? (
            <TextButton tone="muted" onClick={handleSkip} className="text-xs">
              Skip
            </TextButton>
          ) : (
            <span className="w-8" />
          )}
        </div>

        <div className="px-6 overflow-y-auto">
          {/* Hero icon + chip */}
          <div className="mt-1 mb-4 flex items-center gap-3">
            <StepHero Icon={step.icon} large={isExtended} />
            {chip && <StatusChip variant={chip.variant}>{chip.label}</StatusChip>}
          </div>

          <h2
            id="roava-onboarding-title"
            className={cn("font-bold tracking-tight text-[var(--roava-text)]", titleClass)}
          >
            {step.title}
          </h2>
          {bodyText && (
            <p className={cn("mt-2 text-[var(--roava-text-muted)]", bodyClass)}>
              {bodyText}
            </p>
          )}

          {/* Welcome — tour mode picker */}
          {isWelcome && (
            <div className="mt-5 grid grid-cols-1 gap-2">
              <PrimaryButton
                onClick={() => { setTourMode("quick"); setStepIdx(1); }}
                className="w-full h-12 rounded-2xl text-base font-semibold"
              >
                Quick tour <span className="opacity-70 ml-1 text-sm font-normal">· ~1 min</span>
              </PrimaryButton>
              <SecondaryButton
                onClick={() => { setTourMode("extended"); setStepIdx(1); }}
                className="w-full h-12 rounded-2xl text-base font-semibold"
              >
                Take your time <span className="opacity-70 ml-1 text-sm font-normal">· ~4 min</span>
              </SecondaryButton>
              <PrimaryButton
                onClick={() => {
                  markOnboardingComplete();
                  onClose();
                  setTimeout(() => {
                    void import("@/lib/onboarding/tour-state").then((m) => m.startTour("full-walkthrough"));
                  }, 50);
                }}
                className="w-full h-12 rounded-2xl text-base font-semibold"
              >
                Full walkthrough <span className="opacity-70 ml-1 text-sm font-normal">· ~6 min</span>
              </PrimaryButton>
              <p className="text-[11px] text-center text-[var(--roava-text-muted)] -mt-1">
                Real screens, step by step.
              </p>
              <button
                type="button"
                onClick={() => setMiniDemosOpen(true)}
                className="w-full h-12 rounded-2xl border border-border bg-background text-sm font-semibold hover:bg-accent/40 transition"
              >
                Watch mini demos
              </button>
              <p className="mt-1 text-[11px] text-center text-[var(--roava-text-muted)]">
                See short clips of common actions before you set things up.
              </p>
            </div>
          )}

          {/* Showcase mini-preview */}
          {step.showcase && (
            <ShowcaseFrame
              caption={!isExtended ? step.showcaseCaption : undefined}
              captions={isExtended ? step.showcaseCaptions : undefined}
              height={isExtended ? 280 : 220}
            >
              {step.showcase === "globe" && <RouteGlobePreview />}
              {step.showcase === "upload" && <UploadPreview />}
              {step.showcase === "pods" && <PodsPreview />}
              {step.showcase === "timeline" && <TimelinePreview />}
            </ShowcaseFrame>
          )}

          {/* Extended-only setting deep dive */}
          {step.deepDive && <DeepDiveForStep kind={step.deepDive} />}

          {/* Final-step sample trip picker */}
          {isLast && <SampleTripPicker onFinish={finishTour} />}

          {step.perm && kind === "denied" && (
            <p className="mt-3 text-xs text-[var(--roava-red)] leading-snug">
              You previously declined this. Open iOS Settings to allow it.
            </p>
          )}
          {step.perm && (kind === "granted" || kind === "limited") && (
            <p className="mt-3 text-xs text-[var(--roava-green)] leading-snug inline-flex items-center gap-1.5">
              <Check className="size-3.5" /> You're all set for this one.
            </p>
          )}
        </div>

        {/* Footer buttons */}
        {!isWelcome && (
          <div className="px-6 pt-5 pb-2">
            {primary && (
              <PrimaryButton
                onClick={() => { void primary.onClick(); }}
                disabled={pending}
                className="w-full h-12 rounded-2xl text-base font-semibold"
              >
                {primary.label}
              </PrimaryButton>
            )}

            {step.whereHint && !step.deepDive && (
              <p className="mt-2 text-[11px] text-center text-[var(--roava-text-muted)] leading-snug">
                {step.whereHint}
              </p>
            )}

            {isLast && (
              <p className="mt-3 text-[11px] text-center text-[var(--roava-text-muted)] leading-snug">
                Add or remove sample trips anytime in Settings → Account.
              </p>
            )}

            {secondary && (
              <div className="mt-2 flex justify-center">
                <TextButton tone="muted" onClick={secondary.onClick} className="text-xs">
                  {secondary.label}
                </TextButton>
              </div>
            )}

            {/* Mode toggle */}
            {!isLast && tourMode && (
              <div className="mt-1 flex justify-center">
                <TextButton
                  tone="muted"
                  onClick={() => setTourMode(tourMode === "quick" ? "extended" : "quick")}
                  className="text-[11px] opacity-70"
                >
                  Switch to {tourMode === "quick" ? "extended" : "quick"} tour
                </TextButton>
              </div>
            )}
          </div>
        )}
      </div>
      <MiniDemosSheet open={miniDemosOpen} onOpenChange={setMiniDemosOpen} />
    </div>
  );
}

function StepHero({ Icon, large }: { Icon: LucideIcon; large?: boolean }): ReactNode {
  return (
    <div
      className={cn(
        "rounded-2xl bg-[var(--roava-blue)]/10 text-[var(--roava-blue)] grid place-items-center",
        large ? "size-16" : "size-14",
      )}
    >
      <Icon className={large ? "size-8" : "size-7"} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Deep-dive content per kind                                         */
/* ------------------------------------------------------------------ */

function DeepDiveForStep({ kind }: { kind: DeepDiveKind }) {
  switch (kind) {
    case "battery":
      return (
        <SettingDeepDive
          Icon={BatteryCharging}
          rowTitle="Tracking battery guard"
          rowSubtitle="Route tab → Tracking battery guard"
          rowValue="On · 15%"
          rowValueTone="green"
        />
      );
    case "accuracy":
      return (
        <SettingDeepDive
          Icon={Crosshair}
          rowTitle="GPS detail"
          rowSubtitle="Settings → Trip readiness → Route tracking → GPS detail"
          rowValue="Balanced"
          rowValueTone="blue"
        />
      );
    case "uploadLimits":
      return (
        <SettingDeepDive
          Icon={Wifi}
          rowTitle="Upload over cellular"
          rowSubtitle="Settings → Upload & sync settings → Upload rules"
          rowValue="Wi-Fi only"
          rowValueTone="blue"
        />
      );
    case "podGrouping":
      return (
        <SettingDeepDive
          Icon={Layers}
          rowTitle="Pod grouping sensitivity"
          rowSubtitle="Settings → Trip readiness → Pod grouping"
          rowValue="Standard"
          rowValueTone="blue"
        />
      );
    case "quietHours":
      return (
        <SettingDeepDive
          Icon={MoonStar}
          rowTitle="Quiet hours"
          rowSubtitle="Settings → Notifications"
          rowValue="9pm – 7am"
          rowValueTone="blue"
        />
      );
    case "offline":
      return (
        <SettingDeepDive
          Icon={CloudOff}
          rowTitle="Offline route queue"
          rowSubtitle="Settings → Upload & sync settings"
          rowValue="Always on"
          rowValueTone="green"
        />
      );

    case "privacy":
      return (
        <SettingDeepDive
          Icon={ShieldCheck}
          rowTitle="Trip visibility"
          rowSubtitle="Settings → Privacy"
          rowValue="Private"
          rowValueTone="green"
        />
      );
  }
}
